import { DATE_FORMATS } from '@web-platform/shared-universal-constants/src/dateFormats';
import { DATE_TYPES } from '@web-platform/shared-universal-constants/src/dateTypes';
import { PLACE_TYPES } from '@web-platform/shared-universal-constants/src/places';
import { ONE_WAY } from '@web-platform/shared-universal-constants/src/tripTypes';
import {
  formatDateCustom,
  isMonthDate,
  isSpecificDate,
  parseDateTime,
  getCulture,
  getIsDifferentMonth,
} from '@web-platform/shared-universal-utils-frontend-localisation';

import { translate } from '../translate';

import type { PlaceSearchType } from '@web-platform/shared-universal-constants/src/places';
import type { DateString } from '@web-platform/shared-universal-types/src/dateString';
import type { PartialSearchParams } from '@web-platform/shared-universal-types/src/redux/state/appInitial/user/search';
import type { Maybe } from '@web-platform/shared-universal-types-utils/src/utils';

type SearchType = PlaceSearchType;

const formatDateStrings = (
  initialDate: Date,
  finalDate: Date,
  isDifferentMonth: boolean,
  initialMonth: string,
  finalMonth: string,
  isEastAsiaLocales: boolean,
): { initialDateStr: string; finalDateStr: string } => {
  const formatDate = (date: Date, month?: string) => {
    const day = formatDateCustom(date, 'dd');
    const dayOfWeek = formatDateCustom(date, 'EEE');
    return month ? `${dayOfWeek} ${day} ${month}` : `${dayOfWeek} ${day}`;
  };

  const initialDateStr = isEastAsiaLocales
    ? formatDateCustom(initialDate, DATE_FORMATS.ABBR_MONTH_DAY)
    : formatDate(initialDate, isDifferentMonth ? initialMonth : undefined);

  const finalDateStr = isEastAsiaLocales
    ? formatDateCustom(finalDate, DATE_FORMATS.ABBR_MONTH_DAY)
    : formatDate(finalDate, finalMonth);

  return { initialDateStr, finalDateStr };
};

const getOneWayTitle = (
  isWholeMonth: boolean,
  searchType: SearchType,
  initialDate: Date,
  placeName: string,
  initialMonth: string,
  isEastAsiaLocales: boolean,
): string => {
  if (isWholeMonth) {
    if (searchType === PLACE_TYPES.COUNTRY) {
      return translate('ce_search_whole_month', {
        country: placeName,
        month: initialMonth,
      });
    }
    return translate('ce_search_everywhere_whole_month', {
      month: initialMonth,
    });
  }

  const initialDateStr = isEastAsiaLocales
    ? formatDateCustom(initialDate, DATE_FORMATS.ABBR_MONTH_DAY)
    : `${formatDateCustom(initialDate, 'EEE')} ${formatDateCustom(
        initialDate,
        'dd',
      )} ${initialMonth}`;

  if (searchType === PLACE_TYPES.COUNTRY) {
    return translate('ce_search_on_date', {
      country: placeName,
      date: initialDateStr,
    });
  }
  return translate('ce_search_everywhere_on_date', {
    date: initialDateStr,
  });
};

const getReturnTitle = (
  isWholeMonth: boolean,
  searchType: SearchType,
  initialDate: Date,
  finalDate: Date,
  placeName: string,
  initialMonth: string,
  isEastAsiaLocales: boolean,
): string => {
  const finalMonth = formatDateCustom(finalDate, 'MMMM');
  const isDifferentMonth = getIsDifferentMonth(initialMonth, finalMonth);

  if (isWholeMonth) {
    if (!isDifferentMonth) {
      return searchType === PLACE_TYPES.COUNTRY
        ? translate('ce_search_whole_month', {
            country: placeName,
            month: initialMonth,
          })
        : translate('ce_search_everywhere_whole_month', {
            month: initialMonth,
          });
    }
    return searchType === PLACE_TYPES.COUNTRY
      ? translate('ce_search_country_return_whole_month', {
          country: placeName,
          initial_month: initialMonth,
          final_month: finalMonth,
        })
      : translate('ce_search_everywhere_return_whole_month', {
          initial_month: initialMonth,
          final_month: finalMonth,
        });
  }

  const { finalDateStr, initialDateStr } = formatDateStrings(
    initialDate,
    finalDate,
    isDifferentMonth,
    initialMonth,
    finalMonth,
    isEastAsiaLocales,
  );

  return searchType === PLACE_TYPES.COUNTRY
    ? translate('ce_search_country_return_on_date', {
        country: placeName,
        initial_date: initialDateStr,
        final_date: finalDateStr,
      })
    : translate('ce_search_everywhere_return_on_date', {
        initial_date: initialDateStr,
        final_date: finalDateStr,
      });
};

const getCountryOriginSearchTitle = () => translate('ce_select_depart_location');

const getAnytimeSearchTitle = (placeName: string, searchType: SearchType) => {
  if (searchType === PLACE_TYPES.COUNTRY) {
    return translate('ce_explore_country', { country: placeName });
  }
  return translate('ce_explore_everywhere');
};

const getEverywhereAndCountrySearchTitle = (
  tripType: string,
  placeName: string,
  searchType: SearchType,
  outboundDate: DateString,
  inboundDate: DateString,
) => {
  if (!outboundDate) {
    // anytime scene
    return getAnytimeSearchTitle(placeName, searchType);
  }

  const isMonth = isMonthDate(outboundDate);
  const initialDate = parseDateTime(outboundDate);
  const finalDate = parseDateTime(inboundDate);
  const initialMonth = formatDateCustom(initialDate, 'MMMM');

  // TODO: [EXPLORE-302] Combine explore search title optimization
  const { locale } = getCulture();
  const isEastAsiaLocales = ['ko-KR', 'ja-JP', 'zh-CN', 'zh-TW'].includes(locale);

  if (tripType === ONE_WAY) {
    return getOneWayTitle(
      isMonth,
      searchType,
      initialDate,
      placeName,
      initialMonth,
      isEastAsiaLocales,
    );
  }
  return getReturnTitle(
    isMonth,
    searchType,
    initialDate,
    finalDate,
    placeName,
    initialMonth,
    isEastAsiaLocales,
  );
};

export const getSearchTitle = (
  tripType: string,
  placeName: string,
  searchType: Maybe<SearchType>,
  outboundDate: DateString,
  inboundDate: DateString,
): string => {
  if (!searchType) {
    return '';
  }

  if (searchType === PLACE_TYPES.COUNTRY_ORIGIN) {
    return getCountryOriginSearchTitle();
  }

  return getEverywhereAndCountrySearchTitle(
    tripType,
    placeName,
    searchType,
    outboundDate,
    inboundDate,
  );
};

export const getPlaceName = (
  userSearchState: PartialSearchParams,
  placeSearchType: PlaceSearchType | null,
): string => {
  const firstItem = userSearchState.legs && userSearchState.legs[0];
  return placeSearchType === PLACE_TYPES.COUNTRY && firstItem && firstItem.destination
    ? firstItem.destination.name
    : '';
};

export const getPlaceId = (
  userSearchState: PartialSearchParams,
  placeSearchType: PlaceSearchType | null,
) => {
  const firstItem = userSearchState.legs && userSearchState.legs[0];
  return placeSearchType === PLACE_TYPES.COUNTRY && firstItem && firstItem.destination
    ? firstItem.destination.id // Value is ISO code 'ES' or 'IT'
    : '';
};

export const getDateType = (outboundDate: DateString): string => {
  if (isSpecificDate(outboundDate)) {
    return DATE_TYPES.SPECIFIC;
  }
  if (isMonthDate(outboundDate)) {
    return DATE_TYPES.MONTH;
  }
  return DATE_TYPES.ANY_TIME;
};
